import api, { authService } from './api';

export const eventsService = {
  getDisplay: async ({ tab, q, artist, year, startDate, endDate, limit = 1000 } = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/display', {
        params: {
          tab,
          q,
          artist,
          year,
          start_date: startDate,
          end_date: endDate,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data.items || [],
        totalCount: response.data.total_count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch events display',
      };
    }
  },

  getDashboardStats: async (startDate, endDate) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/dashboard-stats', {
        params: { start_date: startDate, end_date: endDate },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return { success: true, items: response.data.items || [] };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch event stats',
      };
    }
  },

  getDashboardOverview: async (eventDate, endDate) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/dashboard-overview', {
        params: { event_date: eventDate, end_date: endDate },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return { success: true, ...response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch the event overview',
      };
    }
  },

  listVendors: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/vendors', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch event vendors',
      };
    }
  },

  getFinancials: async ({ year } = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/financials', {
        params: { year },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch financials',
      };
    }
  },

  updateEvent: async (eventId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/events/${eventId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update event',
      };
    }
  },

  createPosition: async (eventId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post(`/events/${eventId}/positions`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create position',
      };
    }
  },

  getListingOptions: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/listings/options', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch listing options',
      };
    }
  },

  getListings: async (eventRefs = []) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/listings', {
        params: {
          event_refs: eventRefs.join(','),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data.items || [],
        transfers: response.data.transfers || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch listings',
      };
    }
  },

  upsertListing: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.put('/events/listings', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update listing',
      };
    }
  },

  createTransfer: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/listings/transfers', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to add transfer',
      };
    }
  },

  deleteTransfer: async (transferId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.delete(`/events/listings/transfers/${encodeURIComponent(transferId)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to cancel transfer',
      };
    }
  },

  listPlatforms: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/events/platforms', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch event platforms',
      };
    }
  },

  moveOrder: async (orderId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post(`/events/orders/${encodeURIComponent(orderId)}/move`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        error: error.response?.data?.detail || 'Failed to move order',
      };
    }
  },

  movePosition: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/positions/move', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to move position',
      };
    }
  },

  previewMovePosition: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/positions/move/preview', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to preview position move',
      };
    }
  },

  previewEventDateCorrection: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/date-correction/preview', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { success: true, ...response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to preview event date correction',
      };
    }
  },

  correctEventDate: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/date-correction', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { success: true, ...response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to correct event date',
      };
    }
  },

  getCurrencyRates: async () => {
    try {
      const response = await api.get('/events/currency-rates', {
        headers: { Authorization: `Bearer ${authService.getToken()}` },
      });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: 'Unable to load exchange rates.' };
    }
  },

  getUnits: async (eventRef) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get(`/events/units/${encodeURIComponent(eventRef)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch event units',
      };
    }
  },

  previewDeletePosition: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/positions/delete/preview', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to preview position deletion',
      };
    }
  },

  deletePosition: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/positions/delete', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete position',
      };
    }
  },

  getAdjustments: async (eventId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get(`/events/${encodeURIComponent(eventId)}/adjustments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch adjustments',
      };
    }
  },

  replaceAdjustments: async (eventId, items) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.put(
        `/events/${encodeURIComponent(eventId)}/adjustments`,
        { items },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to save adjustments',
      };
    }
  },

  getUserHistory: async (eventId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get(`/events/${encodeURIComponent(eventId)}/user-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch user history',
      };
    }
  },

  updateUnit: async (unitId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/events/units/${encodeURIComponent(unitId)}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update unit',
      };
    }
  },

  createManualPurchase: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/events/manual-purchases', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: (typeof detail === 'string' ? detail : detail?.message) || 'Failed to save manual purchase',
        code: typeof detail === 'object' ? detail?.code : null,
        details: typeof detail === 'object' ? detail : null,
      };
    }
  },

  addPurchaseToPosition: async (eventId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post(`/events/${encodeURIComponent(eventId)}/purchases`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to add purchase to position',
      };
    }
  },
};
